import {
  cashbookCustomNote,
  type CashbookDebtSheetFooter,
  type CashbookDebtSheetRow,
  type CashbookViewMode
} from '@finvox/shared/cashbook';
import { toMoney } from '@finvox/shared/money';
import { format, parseISO } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@/components/ui/IconButton';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { cn } from '@/lib/cn';
import { displayCashbookDebtEntryLabel } from '@/lib/cashbookEntryLabel';
import type { CashbookPeriodDrillTarget } from '@/lib/cashbookPeriodUrl';
import type { TFunction } from 'i18next';

type Props = {
  rows: CashbookDebtSheetRow[];
  footer: CashbookDebtSheetFooter | null;
  loading: boolean;
  initialLoading?: boolean;
  viewMode?: CashbookViewMode;
  canVoid: boolean;
  periodOpen: boolean;
  highlight?: boolean;
  canDrillToDay?: boolean;
  canDrillToMonth?: boolean;
  onPeriodDrillDown?: (target: CashbookPeriodDrillTarget) => void;
  onVoid?: (entryId: string) => void;
};

function rowDescription(
  row: CashbookDebtSheetRow,
  viewMode: CashbookViewMode,
  locale: typeof enUS | typeof ar,
  openingRowLabel: string,
  t: TFunction
): string {
  if (row.source === 'opening_debt') return openingRowLabel;
  if (viewMode === 'month') {
    return format(parseISO(row.date), 'EEEE, PPP', { locale });
  }
  if (viewMode === 'multi' || viewMode === 'year') {
    return format(parseISO(row.date), 'MMMM yyyy', { locale });
  }
  return displayCashbookDebtEntryLabel(row.label, t);
}

function drillTargetForRow(
  row: CashbookDebtSheetRow,
  viewMode: CashbookViewMode
): CashbookPeriodDrillTarget | null {
  if (row.source === 'opening_debt') return null;
  if (viewMode === 'month') return { kind: 'day', date: row.date };
  if (viewMode === 'year' || viewMode === 'multi') {
    return { kind: 'month', month: row.date.slice(0, 7) };
  }
  return null;
}

export function CashbookDebtSheet({
  rows = [],
  footer,
  loading,
  initialLoading = false,
  viewMode = 'day',
  canVoid,
  periodOpen,
  highlight = false,
  canDrillToDay = true,
  canDrillToMonth = true,
  onPeriodDrillDown,
  onVoid
}: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('ar') ? ar : enUS;
  const [expanded, setExpanded] = useState(false);
  const showNotesColumn = viewMode === 'day';
  const colCount = showNotesColumn ? 7 : 6;
  const showOverlay = loading && rows.length > 0;
  const showEmptyLoading = initialLoading && rows.length === 0;
  const showFooter =
    footer != null && (rows.length > 0 || toMoney(footer.endBalance).gt(0));

  function canDrill(target: CashbookPeriodDrillTarget | null): boolean {
    if (!target || !onPeriodDrillDown) return false;
    if (target.kind === 'day') return canDrillToDay;
    return canDrillToMonth;
  }

  function renderDescription(row: CashbookDebtSheetRow) {
    const text = rowDescription(row, viewMode, locale, t('cashbook.debts.openingRow'), t);
    const target = drillTargetForRow(row, viewMode);
    if (!canDrill(target) || !target) return text;
    const aria =
      target.kind === 'day'
        ? t('cashbook.drillToDay', { date: target.date })
        : t('cashbook.drillToMonth', { month: target.month });
    return (
      <button
        type="button"
        className="text-start font-medium text-[var(--fv-primary)] underline-offset-2 hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fv-primary)]"
        aria-label={aria}
        onClick={() => onPeriodDrillDown?.(target)}
      >
        {text}
      </button>
    );
  }

  return (
    <div className="mt-8">
      <div className="mb-3 flex items-center gap-1.5">
        <h2 className="text-lg font-semibold">{t('cashbook.debts.ledgerTitle')}</h2>
        <IconButton
          aria-label={
            expanded ? t('cashbook.debts.ledgerCollapse') : t('cashbook.debts.ledgerExpand')
          }
          aria-expanded={expanded}
          variant="ghost"
          size="sm"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </IconButton>
      </div>
      {expanded ? (
        <div
          className={cn(
            'relative rounded-lg border border-[var(--fv-border)] transition-[box-shadow] duration-500',
            highlight && 'ring-2 ring-[var(--fv-primary)] ring-offset-2 ring-offset-[var(--fv-bg)]'
          )}
        >
          {showOverlay ? (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center bg-[var(--fv-surface)]/70"
              aria-live="polite"
              aria-busy="true"
            >
              <div className="flex items-center gap-2 text-sm text-[var(--fv-text-muted)]">
                <span
                  className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-[var(--fv-border)] border-t-[var(--fv-primary)]"
                  aria-hidden
                />
                {t('common.loading')}
              </div>
            </div>
          ) : null}

          <div className="md:hidden">
            {showEmptyLoading ? (
              <div className="flex flex-col items-center gap-2 p-12 text-center text-[var(--fv-text-muted)]">
                <span
                  className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-[var(--fv-border)] border-t-[var(--fv-primary)]"
                  aria-hidden
                />
                {t('common.loading')}
              </div>
            ) : rows.length === 0 ? (
              <div className="p-8 text-center text-[var(--fv-text-muted)]">{t('common.noResults')}</div>
            ) : (
              <ul className="divide-y divide-[var(--fv-border)]">
                {rows.map((row, i) => {
                  const note = showNotesColumn
                    ? cashbookCustomNote(row.memo, row.categoryKey)
                    : undefined;
                  const showVoid =
                    canVoid &&
                    periodOpen &&
                    row.debtEntryId &&
                    row.source === 'debt' &&
                    !row.voided;

                  return (
                    <li
                      key={row.id}
                      className={cn(
                        'p-3',
                        i % 2 === 1 && 'bg-[var(--fv-bg)]/50',
                        row.voided && 'opacity-50 line-through decoration-[var(--fv-text-muted)]'
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-xs tabular-nums text-[var(--fv-text-muted)]">{row.date}</p>
                        {showVoid ? (
                          <IconButton
                            aria-label={t('cashbook.void')}
                            variant="ghost"
                            size="sm"
                            onClick={() => onVoid?.(row.debtEntryId!)}
                          >
                            ×
                          </IconButton>
                        ) : null}
                      </div>
                      <div className="mt-1 break-words font-medium text-[var(--fv-text)]">
                        {renderDescription(row)}
                      </div>
                      {note ? (
                        <p className="mt-1 break-words text-sm text-[var(--fv-text-muted)]">
                          <span className="font-medium text-[var(--fv-text)]">
                            {t('cashbook.colNotes')}:{' '}
                          </span>
                          {note}
                        </p>
                      ) : null}
                      <dl className="mt-2 grid grid-cols-3 gap-x-2 gap-y-2 text-sm">
                        <div>
                          <dt className="text-xs text-[var(--fv-text-muted)]">
                            {t('cashbook.debts.colIncurred')}
                          </dt>
                          <dd className="mt-0.5 text-end tabular-nums">
                            {row.incurredAmount !== '0.00' ? (
                              <MoneyDisplay value={row.incurredAmount} />
                            ) : (
                              '—'
                            )}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-[var(--fv-text-muted)]">
                            {t('cashbook.debts.colPaid')}
                          </dt>
                          <dd className="mt-0.5 text-end tabular-nums">
                            {row.paidAmount !== '0.00' ? (
                              <MoneyDisplay value={row.paidAmount} />
                            ) : (
                              '—'
                            )}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.colBalance')}</dt>
                          <dd className="mt-0.5 text-end font-medium tabular-nums">
                            <MoneyDisplay value={row.balance} />
                          </dd>
                        </div>
                      </dl>
                    </li>
                  );
                })}
              </ul>
            )}
            {showFooter ? (
              <div className="border-t-2 border-[var(--fv-border)] bg-[var(--fv-surface)] p-3 font-semibold">
                <p className="text-sm">{t('cashbook.footerPeriodTotals')}</p>
                <dl className="mt-2 grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
                  <div>
                    <dt className="text-xs font-normal text-[var(--fv-text-muted)]">
                      {t('cashbook.debts.colIncurred')}
                    </dt>
                    <dd className="mt-0.5 text-end tabular-nums">
                      <MoneyDisplay value={footer.totalIncurred} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-normal text-[var(--fv-text-muted)]">
                      {t('cashbook.debts.colPaid')}
                    </dt>
                    <dd className="mt-0.5 text-end tabular-nums">
                      <MoneyDisplay value={footer.totalPaid} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-normal text-[var(--fv-text-muted)]">
                      {t('cashbook.footerEndBalance')}
                    </dt>
                    <dd className="mt-0.5 text-end tabular-nums">
                      <MoneyDisplay value={footer.endBalance} />
                    </dd>
                  </div>
                </dl>
              </div>
            ) : null}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-[640px] table-fixed text-sm">
              <colgroup>
                <col className="w-[7.5rem]" />
                <col />
                {showNotesColumn ? <col /> : null}
                <col className="w-[6.5rem]" />
                <col className="w-[6.5rem]" />
                <col className="w-[6.5rem]" />
                <col className="w-10" />
              </colgroup>
              <thead className="sticky top-0 bg-[var(--fv-surface)]">
                <tr className="border-b-2 border-[var(--fv-border)] text-start text-[var(--fv-text)]">
                  <th className="p-2.5 text-start text-xs font-semibold uppercase tracking-wide">
                    {t('cashbook.colDate')}
                  </th>
                  <th className="p-2.5 text-start text-xs font-semibold uppercase tracking-wide">
                    {t('cashbook.colDescription')}
                  </th>
                  {showNotesColumn ? (
                    <th className="p-2.5 text-start text-xs font-semibold uppercase tracking-wide">
                      {t('cashbook.colNotes')}
                    </th>
                  ) : null}
                  <th className="p-2.5 text-end text-xs font-semibold uppercase tracking-wide tabular-nums">
                    {t('cashbook.debts.colIncurred')}
                  </th>
                  <th className="p-2.5 text-end text-xs font-semibold uppercase tracking-wide tabular-nums">
                    {t('cashbook.debts.colPaid')}
                  </th>
                  <th className="p-2.5 text-end text-xs font-semibold uppercase tracking-wide tabular-nums">
                    {t('cashbook.colBalance')}
                  </th>
                  <th className="p-2.5" aria-hidden />
                </tr>
              </thead>
              <tbody>
                {showEmptyLoading ? (
                  <tr>
                    <td colSpan={colCount} className="p-8 text-center text-[var(--fv-text-muted)]">
                      {t('common.loading')}
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={colCount} className="p-8 text-center text-[var(--fv-text-muted)]">
                      {t('common.noResults')}
                    </td>
                  </tr>
                ) : (
                  rows.map((row, i) => {
                    const note = showNotesColumn
                      ? cashbookCustomNote(row.memo, row.categoryKey)
                      : undefined;
                    return (
                    <tr
                      key={row.id}
                      className={cn(
                        'border-t border-[var(--fv-border)]',
                        i % 2 === 1 && 'bg-[var(--fv-bg)]/50',
                        row.voided && 'opacity-50 line-through decoration-[var(--fv-text-muted)]'
                      )}
                    >
                      <td className="p-2.5 whitespace-nowrap align-top tabular-nums">{row.date}</td>
                      <td className="p-2.5 align-top break-words">{renderDescription(row)}</td>
                      {showNotesColumn ? (
                        <td className="p-2.5 align-top break-words text-[var(--fv-text-muted)]">
                          {note ?? '—'}
                        </td>
                      ) : null}
                      <td className="p-2.5 text-end align-top tabular-nums">
                        {row.incurredAmount !== '0.00' ? (
                          <MoneyDisplay value={row.incurredAmount} />
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="p-2.5 text-end align-top tabular-nums">
                        {row.paidAmount !== '0.00' ? <MoneyDisplay value={row.paidAmount} /> : '—'}
                      </td>
                      <td className="p-2.5 text-end align-top font-medium tabular-nums">
                        <MoneyDisplay value={row.balance} />
                      </td>
                      <td className="p-2.5 align-top">
                        {canVoid &&
                        periodOpen &&
                        row.debtEntryId &&
                        row.source === 'debt' &&
                        !row.voided ? (
                          <IconButton
                            aria-label={t('cashbook.void')}
                            variant="ghost"
                            size="sm"
                            onClick={() => onVoid?.(row.debtEntryId!)}
                          >
                            ×
                          </IconButton>
                        ) : null}
                      </td>
                    </tr>
                  );
                  })
                )}
              </tbody>
              {showFooter ? (
                <tfoot className="border-t-2 border-[var(--fv-border)] bg-[var(--fv-surface)] font-semibold">
                  <tr>
                    <td className="p-2.5" colSpan={showNotesColumn ? 3 : 2}>
                      {t('cashbook.footerPeriodTotals')}
                    </td>
                    <td className="p-2.5 text-end tabular-nums">
                      <MoneyDisplay value={footer.totalIncurred} />
                    </td>
                    <td className="p-2.5 text-end tabular-nums">
                      <MoneyDisplay value={footer.totalPaid} />
                    </td>
                    <td className="p-2.5 text-end tabular-nums" title={t('cashbook.footerEndBalance')}>
                      <span className="sr-only">{t('cashbook.footerEndBalance')}: </span>
                      <MoneyDisplay value={footer.endBalance} />
                    </td>
                    <td />
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
